import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Phone,
  Mail,
  MapPin,
  Navigation,
  Clock,
  ChevronRight,
  Star,
  Calendar,
  Send,
  Users,
} from "lucide-react";
import Testimonials from "../components/Testimonials";

const ContactPage = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
      mirror: true,
    });
  }, []);

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    company: "The Gulmohar Grand",
  });

  const handleChange = (e) => {
    setFormState({
      ...formState,
      [e.target.id]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch("https://apigulmohar.dodunsoftsolutions.com/api/contact-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formState.name,
          email: formState.email,
          phone: formState.phone,
          subject: formState.subject,
          message: formState.message,
          company: formState.company,
        }),
      });

      const result = await response.json();
      if (result.success) {
        alert("Message sent successfully!");
        setFormState({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          company: "The Gulmohar Grand",
        });
      } else {
        alert("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-serif text-gray-800">
      {/* Elegant Full-Screen Hero Section */}
      <div className="relative h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transform scale-105 transition-transform duration-10000"
          style={{
            backgroundImage:
              "url('https://gulmohargrandhotel.com/wp-content/uploads/2019/12/Best-hotel-near-una-HP1-1024x683.jpg')",
            animation: "slowZoom 30s infinite alternate",
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>

        {/* Elegant overlay pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10"></div>

        <div
          className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center overflow-hidden"
          data-aos="fade-up"
          data-aos-duration="1500"
        >
          <div className="mb-6">
            <div className="flex items-center justify-center">
              <div className="w-12 h-px bg-amber-300 mr-4"></div>
              <p className="text-amber-300 font-light tracking-widest uppercase">
                Experience Luxury
              </p>
              <div className="w-12 h-px bg-amber-300 ml-4"></div>
            </div>
          </div>
          <h1 className="text-5xl md:text-7xl font-light text-white mb-4 tracking-wider">
            CONTACT{" "}
            <span className="font-serif italic text-amber-300">
              & Inquiries
            </span>
          </h1>
          <p className="max-w-2xl text-lg md:text-xl text-gray-200 leading-relaxed mt-4 font-light">
            Discover the epitome of luxury and comfort in the heart of Himachal
            Pradesh
          </p>

          <div className="mt-12 flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
            <Link
              to="/room-booking"
              className="group px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white border border-amber-500 rounded-none transition-all duration-300 overflow-hidden relative"
            >
              <span className="relative z-10 font-light tracking-wider uppercase flex items-center">
                Reserve Your Stay{" "}
                <ChevronRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
              <div className="absolute inset-0 bg-amber-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </Link>
            {/* <Link to="/virtual-tour" className="px-8 py-4 border border-white text-white hover:bg-white hover:text-amber-900 rounded-none transition-all duration-300 uppercase tracking-wider font-light">
              Take a Tour
            </Link> */}
          </div>
        </div>

        {/* Elegant floating info bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-white/10 backdrop-blur-md border-t border-white/20">
          <div className="container mx-auto flex flex-col md:flex-row items-center justify-between py-4 px-4 md:px-10 text-white">
            <div
              className="flex items-center mb-4 md:mb-0 overflow-hidden"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <Phone className="h-5 w-5 text-amber-300 mr-3" />
              <span className="text-sm tracking-wider">+91-62305 62306</span>
            </div>
            <div
              className="flex items-center mb-4 md:mb-0 overflow-hidden"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <Mail className="h-5 w-5 text-amber-300 mr-3" />
              <span className="text-sm tracking-wider">
                info@gulmohargrandhotel.com
              </span>
            </div>
            <div
              className="flex items-center overflow-hidden"
              data-aos="fade-right"
              data-aos-delay="400"
            >
              <MapPin className="h-5 w-5 text-amber-300 mr-3" />
              <span className="text-sm tracking-wider">
                8 Miles Stone, Behdala, Una, HP
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Luxury Introduction Section */}
      <div className="py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto px-4">
          <div
            className="max-w-4xl mx-auto text-center overflow-hidden"
            data-aos="fade-up"
          >
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-px bg-amber-600 mr-4"></div>
              <Star className="text-amber-600 h-8 w-8" />
              <div className="w-12 h-px bg-amber-600 ml-4"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-light text-amber-900 mb-6 tracking-wide">
              EXQUISITE COMFORT & ELEGANCE
            </h2>
            <p className="text-lg leading-relaxed text-gray-700 mb-10">
              At The Gulmohar Grand, we pride ourselves on delivering
              unparalleled service and luxury. Our dedicated concierge team is
              available 24/7 to ensure your stay is nothing short of
              extraordinary. We invite you to reach out for any inquiries,
              special requests, or to begin planning your perfect getaway in the
              foothills of the Himalayas.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <div className="flex flex-col items-center px-6 py-3 border-r border-amber-200 last:border-0">
                <div className="bg-amber-100 p-3 rounded-full mb-3">
                  <Calendar className="h-6 w-6 text-amber-700" />
                </div>
                <span className="text-amber-900 font-semibold">
                  24/7 Concierge
                </span>
              </div>
              <div className="flex flex-col items-center px-6 py-3 border-r border-amber-200 last:border-0">
                <div className="bg-amber-100 p-3 rounded-full mb-3">
                  <Users className="h-6 w-6 text-amber-700" />
                </div>
                <span className="text-amber-900 font-semibold">
                  Personalized Service
                </span>
              </div>
              <div className="flex flex-col items-center px-6 py-3 border-r border-amber-200 last:border-0">
                <div className="bg-amber-100 p-3 rounded-full mb-3">
                  <Send className="h-6 w-6 text-amber-700" />
                </div>
                <span className="text-amber-900 font-semibold">
                  Swift Response
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Elegant Contact Form and Information Section */}
      <div className="py-20 bg-[url('https://www.transparenttextures.com/patterns/brushed-alum.png')] bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
            {/* Contact Form */}
            <div
              data-aos="fade-right"
              data-aos-delay="100"
              className="order-2 md:order-1 overflow-hidden"
            >
              <div className="mb-12">
                <h3 className="text-xs tracking-widest uppercase text-amber-600 mb-3">
                  Reach Out to Us
                </h3>
                <h2 className="text-3xl font-light text-gray-800 mb-6">
                  Send a Message
                </h2>
                <div className="w-20 h-1 bg-amber-600"></div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <input
                      type="text"
                      id="name"
                      value={formState.name}
                      onChange={handleChange}
                      className="w-full bg-gray-50 border-b-2 border-gray-300 focus:border-amber-600 px-4 py-3 outline-none transition-colors duration-300"
                      placeholder="Your Name"
                      required
                    />
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      id="phone"
                      value={formState.phone}
                      onChange={handleChange}
                      className="w-full bg-gray-50 border-b-2 border-gray-300 focus:border-amber-600 px-4 py-3 outline-none transition-colors duration-300"
                      placeholder="Mobile Number"
                      required
                    />
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    value={formState.email}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border-b-2 border-gray-300 focus:border-amber-600 px-4 py-3 outline-none transition-colors duration-300"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <div className="relative">
                  <input
                    type="text"
                    id="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border-b-2 border-gray-300 focus:border-amber-600 px-4 py-3 outline-none transition-colors duration-300"
                    placeholder="Subject"
                    required
                  />
                </div>

                <div className="relative">
                  <textarea
                    id="message"
                    rows="5"
                    value={formState.message}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border-b-2 border-gray-300 focus:border-amber-600 px-4 py-3 outline-none transition-colors duration-300 resize-none"
                    placeholder="Your Message"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="group px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white border border-amber-500 transition-all duration-300 overflow-hidden relative inline-flex items-center"
                >
                  <span className="relative z-10 font-light tracking-wider uppercase">
                    Send Message
                  </span>
                  <div className="absolute inset-0 bg-amber-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div
              data-aos="fade-right overflow-hidden"
              data-aos-delay="200"
              className="order-1 md:order-2 overflow-hidden"
            >
              <div className="relative">
                <div className="absolute -top-10 -left-10 w-40 h-40 border border-amber-200 z-0"></div>
                <div className="relative z-10 bg-white p-8 shadow-2xl">
                  <div className="mb-12">
                    <h3 className="text-xs tracking-widest uppercase text-amber-600 mb-3">
                      How to Find Us
                    </h3>
                    <h2 className="text-3xl font-light text-gray-800 mb-6">
                      Contact Details
                    </h2>
                    <div className="w-20 h-1 bg-amber-600"></div>
                  </div>

                  <div className="space-y-8">
                    <div className="flex">
                      <div className="flex-shrink-0 mr-6">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-amber-700" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">
                          Hotel Address
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          <a
                            href="https://devbhoomigroup.in"
                            target="_blank"
                            rel="noopener noreferrer"
                            className=" hover:underline"
                          >
                            Dev Bhoomi Group Companies
                          </a>
                          ,
                          <br />
                          Admin Block at The Gulmohar Grand,
                          <br />
                          8 mile stone, Una Chandigarh
                          <br />
                          Highway Behdala, Una,
                          <br />
                          Himachal Pradesh 174306
                        </p>
                      </div>
                    </div>

                    <div className="flex">
                      <div className="flex-shrink-0 mr-6">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <Phone className="h-6 w-6 text-amber-700" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">
                          Phone Numbers
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          <a
                            href="tel:+916230562306"
                            className="hover:underline"
                          >
                            +91-62305 62306
                          </a>
                          <br />
                          <a
                            href="tel:+916230562314"
                            className="hover:underline"
                          >
                            +91-062305 62314
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="flex">
                      <div className="flex-shrink-0 mr-6">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                          <Mail className="h-6 w-6 text-amber-700" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">
                          Email Address
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          <a
                            href="mailto:info@gulmohargrandhotel.com"
                            className="hover:underline"
                          >
                            info@gulmohargrandhotel.com
                          </a>
                          <br />
                          <a
                            href="mailto:gm@gulmohargrandhotel.com"
                            className="hover:underline"
                          >
                            gm@gulmohargrandhotel.com
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10 pt-10 border-t border-gray-200">
                    <h3 className="font-semibold text-lg mb-4">
                      Available Hours
                    </h3>
                    <div className="flex justify-between text-gray-600 mb-2">
                      <span>Monday - Friday:</span>
                      <span>24 Hours</span>
                    </div>
                    <div className="flex justify-between text-gray-600 mb-2">
                      <span>Saturday:</span>
                      <span>24 Hours</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Sunday:</span>
                      <span>24 Hours</span>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-10 -right-10 w-40 h-40 border border-amber-200 z-0"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Elegant Map Section with Directions */}
      <div className="py-20 bg-[url('https://www.transparenttextures.com/patterns/elegant-grid.png')] bg-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 overflow-hidden" data-aos="fade-up">
            <h3 className="text-xs tracking-widest uppercase text-amber-600 mb-3">
              Find Your Way
            </h3>
            <h2 className="text-3xl md:text-4xl font-light text-gray-800 mb-6">
              How to Reach Us
            </h2>
            <div className="w-20 h-1 bg-amber-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div
              className="bg-white p-8 shadow-xl relative overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="absolute top-0 left-0 h-1 w-full bg-amber-600"></div>
              <div className="mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <Navigation className="h-8 w-8 text-amber-700" />
                </div>
              </div>
              <h3 className="text-xl font-medium text-center mb-4">By Car</h3>
              <p className="text-gray-600 text-center mb-6">
                The Gulmohar Grand is located on Una Nangal Highway (SH 25) at 8
                mile stone.
              </p>
            </div>

            <div
              className="bg-white p-8 shadow-xl relative overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="absolute top-0 left-0 h-1 w-full bg-amber-600"></div>
              <div className="mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <Phone className="h-8 w-8 text-amber-700" />
                </div>
              </div>
              <h3 className="text-xl font-medium text-center mb-4">
                Need Assistance?
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Do you have any wishes or questions? Our concierge team is
                available 24/7 to assist with all your travel needs.
              </p>
              <div className="text-center">
                <a
                  href="tel:+916230562306"
                  className="inline-flex items-center text-amber-600 hover:text-amber-800 font-medium"
                >
                  Call Now
                  <ChevronRight className="ml-1 h-4 w-4" />
                </a>
              </div>
            </div>

            <div
              className="bg-white p-8 shadow-xl relative overflow-hidden transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="absolute top-0 left-0 h-1 w-full bg-amber-600"></div>
              <div className="mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="h-8 w-8 text-amber-700" />
                </div>
              </div>
              <h3 className="text-xl font-medium text-center mb-4">By Train</h3>
              <p className="text-gray-600 text-center mb-6">
                20 minutes drive from Una, Himachal railway Station and Nangal
                Dam railway Station. Private transfers available on request.
              </p>
            </div>
          </div>

          <div className="relative overflow-hidden" data-aos="zoom-in">
            <div className="absolute -top-4 -left-4 w-40 h-40 border border-amber-200 z-0"></div>
            <div className="w-full h-96 rounded-none overflow-hidden shadow-2xl relative z-10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3404.271432527202!2d76.32299110000001!3d31.4341936!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391adb9599ee856d%3A0x596906d366fdf582!2sThe%20Gulmohar%20Grand%20Hotel%20-%208%20miles%20stone%20Una%20Behdala!5e0!3m2!1sen!2sin!4v1746033228329!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="The Gulmohar Grand Location"
              ></iframe>
            </div>
            <div className="absolute -bottom-4 -right-4 w-40 h-40 border border-amber-200 z-0"></div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Add custom CSS for slow zoom animation */}
      <style jsx>{`
        @keyframes slowZoom {
          from {
            transform: scale(1);
          }
          to {
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
